import axios from 'axios';
import config from '../../config';
import logout from '../../utils/auth/logout';

const api = axios.create({
    baseURL: config.API_SERVER
});

/**
 * REQUEST → adiciona access token
 */
api.interceptors.request.use((config) => {
    // Normaliza URLs para evitar duplicação do prefixo `/api/`.
    // Ex.: baseURL = "https://.../api/" + url = "api/v1/..." -> ".../api/api/v1/..."
    // Aqui transformamos "api/v1/..." (ou "/api/v1/...") em "v1/...".
    const baseURL = String(config.baseURL ?? '');
    const url = config.url;
    const baseEndsWithApi = /\/api\/?$/.test(baseURL.replace(/\/+$/, ''));

    if (baseEndsWithApi && typeof url === 'string' && (url.startsWith('api/') || url.startsWith('/api/'))) {
        config.url = url.replace(/^\/?api\//, '');
    }

    const token = localStorage.getItem('access_token');
    // console.log(`interceptors - access_token:${token}`);

    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) prom.reject(error);
        else prom.resolve(token);
    });
    failedQueue = [];
};

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // ⚠️ Se não for 401, sai
        if (error.response?.status !== 401) {
            // LOG TEMPORÁRIO - investigação "dados somem depois de carregar"
            if (error.response) {
                console.warn('[DIAG] resposta com erro não-401', {
                    url: originalRequest?.url,
                    status: error.response.status,
                    data: error.response.data
                });
            } else {
                console.warn('[DIAG] erro sem resposta (rede/CORS/timeout?)', {
                    url: originalRequest?.url,
                    message: error.message
                });
            }
            return Promise.reject(error);
        }

        console.warn('[DIAG] 401 recebido', { url: originalRequest?.url, retry: !!originalRequest._retry });

        // ⚠️ Evita loop - se a requisição já foi reenviada com um token novo
        // e ainda assim voltou 401, não adianta tentar de novo
        if (originalRequest._retry) {
            console.warn('[DIAG] logout - 401 mesmo depois de retry com token novo', { url: originalRequest?.url });
            logout();
            return Promise.reject(error);
        }

        // ⚠️ Nunca tenta refresh se o erro veio do próprio refresh
        if (originalRequest.url.includes('token/refresh')) {
            console.warn('[DIAG] logout - o próprio /token/refresh/ voltou 401');
            logout();
            return Promise.reject(error);
        }

        const refresh = localStorage.getItem('refresh_token');

        // ⚠️ Sem refresh → logout
        if (!refresh) {
            console.warn('[DIAG] logout - sem refresh_token no localStorage');
            logout();
            return Promise.reject(error);
        }

        originalRequest._retry = true;

        // Telas como a de conversas fazem várias chamadas em paralelo
        // (polling a cada poucos segundos) - quando o access token expira,
        // todas elas recebem 401 quase ao mesmo tempo. Sem essa fila, cada
        // uma dispararia sua própria chamada de refresh concorrente; aqui,
        // só a primeira chamada refresh de verdade, as demais esperam o
        // resultado dela e reusam o token novo.
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            }).then((newAccess) => {
                originalRequest.headers.Authorization = `Bearer ${newAccess}`;
                return api(originalRequest);
            });
        }

        isRefreshing = true;

        try {
            const res = await axios.post(`${config.API_SERVER}token/refresh/`, { refresh });

            const newAccess = res.data.access;
            // Com ROTATE_REFRESH_TOKENS ativo no backend, cada refresh vem
            // acompanhado de um refresh token novo - precisa ser salvo, senão
            // a sessão continua com validade fixa em vez de "deslizante"
            // (renovando enquanto o usuário estiver ativo).
            const newRefresh = res.data.refresh;

            localStorage.setItem('access_token', newAccess);
            if (newRefresh) {
                localStorage.setItem('refresh_token', newRefresh);
            }

            api.defaults.headers.common.Authorization = `Bearer ${newAccess}`;
            api.defaults.headers.Authorization = `Bearer ${newAccess}`;
            originalRequest.headers.Authorization = `Bearer ${newAccess}`;

            console.warn('[DIAG] refresh OK, reenviando', { url: originalRequest?.url });
            processQueue(null, newAccess);

            return api(originalRequest);
        } catch (err) {
            console.warn('[DIAG] logout - /token/refresh/ falhou', {
                status: err.response?.status,
                data: err.response?.data
            });
            processQueue(err, null);
            logout();
            return Promise.reject(err);
        } finally {
            isRefreshing = false;
        }
    }
);

let isLoggingOut = false;


export default api;


// function logout() {
//     if (isLoggingOut) return;
//     isLoggingOut = true;

//     localStorage.removeItem('access_token');
//     localStorage.removeItem('refresh_token');

//     // dispara evento global
//     window.dispatchEvent(new Event('logout'));
// }
